import * as cognito from "aws-amplify/auth"
import * as gatedSchema from "ipctg-inflow-types/InFlowGatedAPI"
import * as parkSchema from "ipctg-inflow-types/InFlowParkAPI"
import * as reserveSchema from "ipctg-inflow-types/InFlowReserveAPI"

import * as expectedErrors from "@/utilities/expectedErrors"
import { post, put } from "@/utilities/fetch++"
import * as rsp from "@/utilities/types/rsp"

import { resolveApiMode } from "./config"
import { getCurrentEpochMillis } from "./etc"
import { WelcomeToken } from "./types/local"
import welcomeTokenStorage from "./WelcomeTokenStorage"

const api = {
  auth: {
    confirmSignIn: async (input: cognito.ConfirmSignInInput) => {
      const confirmSignInResult = await cognito.confirmSignIn(input)
      return confirmSignInResult
    },
    confirmSignUp: async (input: cognito.ConfirmSignUpInput) => {
      const confirmSignUpResult = await cognito.confirmSignUp(input)
      return confirmSignUpResult
    },
    isAuthenticated: async (): Promise<boolean> => {
      try {
        return !!(await cognito.getCurrentUser())
      } catch {
        return false
      }
    },
    resendSignUpCode: async (input: cognito.ResendSignUpCodeInput) => {
      const result = await cognito.resendSignUpCode(input)
      return result
    },
    fetchAuthSession: async (): Promise<
      | { cognitoIDToken: string | undefined; error?: never }
      | { cognitoIDToken?: never; error: cognito.AuthError }
    > => {
      try {
        const cognitoIDToken = (
          await cognito.fetchAuthSession()
        ).tokens?.idToken?.toString()
        return { cognitoIDToken }
      } catch (error: any) {
        return { error }
      }
    },
    getCognitoIDToken: async (): Promise<string | null> => {
      try {
        const authSession = await cognito.fetchAuthSession()
        return authSession.tokens?.idToken?.toString() || null
      } catch {
        return null
      }
    },
    forgotPassword: async (args: cognito.ResetPasswordInput) => {
      return cognito.resetPassword(args)
    },
    resetPassword: async (input: cognito.ConfirmResetPasswordInput) => {
      return cognito.confirmResetPassword(input)
    },
    signIn: async (
      input: cognito.SignInInput
    ): Promise<cognito.SignInOutput> => {
      const result = await cognito.signIn(input)
      return result
    },
    signUp: async (
      input: cognito.SignUpInput
    ): Promise<cognito.SignUpOutput> => {
      const result = await cognito.signUp(input)
      return result
    },
    autoSignIn: async () => {
      const result = await cognito.autoSignIn()
      return result
    },
    signOut: async (): Promise<void> => {
      return cognito.signOut()
    },
    fetchUserAttributes: async () => {
      return cognito.fetchUserAttributes()
    },
    updateUserAttributes: async (attributes: Record<string, string>) => {
      return cognito.updateUserAttributes({
        userAttributes: attributes,
      })
    },
    updatePassword: async (input: cognito.UpdatePasswordInput) => {
      return cognito.updatePassword(input)
    },
  },
  account: {
    POST: async (request: gatedSchema.GatedAccountPOSTRequest) =>
      post<
        | gatedSchema.GatedAccountPOSTSuccessResponse
        | gatedSchema.GatedAccountPOSTErrorResponse
      >({
        url: "/account",
        data: request,
      }),
  },
  appUser: {
    PUT: async (request: gatedSchema.GatedAppUserPUTRequest) =>
      put<
        | gatedSchema.GatedAppUserPUTSuccessResponse
        | gatedSchema.GatedAppUserPUTErrorResponse
      >({
        url: "/appuser",
        data: request,
      }),
  },
  autoPaySelectCard: {
    POST: async (request: gatedSchema.GatedAutoPaySelectCardPOSTRequest) =>
      post<
        | gatedSchema.GatedAutoPaySelectCardPOSTSuccessResponse
        | gatedSchema.GatedAutoPaySelectCardPOSTErrorResponse
      >({
        url: "/auto-pay-select-card",
        data: request,
      }),
  },
  confirmSetupIntentWallet: {
    POST: async (
      request: gatedSchema.GatedConfirmSetupIntentWalletPOSTRequest
    ) =>
      post<
        | gatedSchema.GatedConfirmSetupIntentWalletPOSTSuccessResponse
        | gatedSchema.GatedConfirmSetupIntentWalletPOSTErrorResponse
      >({
        url: "/confirm-setup-intent-wallet",
        apiMode: "gated",
        data: request,
      }),
  },
  gated: {
    checkout: {
      POST: async (request: gatedSchema.GatedCheckoutPOSTRequest) =>
        post<rsp.GatedCheckoutResponse>({
          url: "/checkout",
          apiMode: "gated",
          data: request,
        }),
    },
    confirmSetupIntent: {
      POST: async (request: gatedSchema.GatedConfirmSetupIntentPOSTRequest) =>
        post<rsp.GatedConfirmSetupIntentResponse>({
          url: "/confirm-setup-intent",
          apiMode: "gated",
          data: request,
        }),
    },
    emailConfirmation: {
      POST: async (request: gatedSchema.GatedEmailConfirmationPOSTRequest) =>
        post<rsp.GatedEmailConfirmationResponse>({
          url: "/email-confirmation",
          apiMode: "gated",
          data: request,
        }),
    },
    getConfirmationDetails: {
      POST: async (
        request: gatedSchema.GatedGetConfirmationDetailsPOSTRequest
      ) =>
        post<rsp.GatedGetConfirmationDetailsResponse>({
          url: "/get-confirmation-details",
          apiMode: "gated",
          data: request,
        }),
    },
    lookupSessions: {
      POST: async (request: gatedSchema.GatedLookupSessionsPOSTRequest) =>
        post<rsp.GatedLookupSessionsResponse>({
          url: "/lookup-sessions",
          apiMode: "gated",
          data: request,
        }),
    },
    pay: {
      POST: async (request: gatedSchema.GatedPayPOSTRequest) =>
        post<rsp.GatedPayPOSTResponse>({
          url: "/pay",
          apiMode: "gated",
          data: request,
        }),
    },
    paymentIntent: {
      POST: async (request: gatedSchema.GatedPaymentIntentPOSTRequest) =>
        post<rsp.GatedPaymentIntentPOSTResponse>({
          url: "/payment-intent",
          apiMode: "gated",
          data: request,
        }),
    },
    promoPayment: {
      POST: async (request: gatedSchema.GatedPromoPaymentPOSTRequest) =>
        post<rsp.GatedPromoPaymentResponse>({
          url: "/promo-payment",
          apiMode: "gated",
          data: request,
        }),
    },
    promoRegister: {
      POST: async (request: gatedSchema.GatedPromoRegisterPOSTRequest) =>
        post<rsp.GatedPromoRegisterResponse>({
          url: "/promo-register",
          apiMode: "gated",
          data: request,
        }),
    },
    registerSession: {
      POST: async (request: gatedSchema.GatedRegisterSessionPOSTRequest) =>
        post<rsp.GatedRegisterSessionResponse>({
          url: "/register-session",
          apiMode: "gated",
          data: request,
        }),
    },
    sessionDetails: {
      POST: async (request: gatedSchema.GatedSessionDetailsPOSTRequest) =>
        post<
          | gatedSchema.GatedSessionDetailsPOSTSuccessResponse
          | gatedSchema.GatedSessionDetailsPOSTErrorResponse
        >({
          url: "/session-details",
          apiMode: "gated",
          data: request,
        }),
    },
    setupIntent: {
      POST: async (request: gatedSchema.GatedSetupIntentPOSTRequest) =>
        post<rsp.GatedSetupIntentResponse>({
          url: "/setup-intent",
          apiMode: "gated",
          data: request,
        }),
    },
    tagCash: {
      POST: async (request: gatedSchema.GatedTagCashPOSTRequest) =>
        post<rsp.GatedTagCashPOSTResponse>({
          url: "/tag-cash",
          apiMode: "gated",
          data: request,
        }),
    },
    textConfirmation: {
      POST: async (request: gatedSchema.GatedTextConfirmationPOSTRequest) =>
        post<rsp.GatedTextConfirmationResponse>({
          url: "/text-confirmation",
          apiMode: "gated",
          data: request,
        }),
    },
  },

  // request type is the same for both gated and park variants. See: ParkEmailPOSTRequest
  emailReceipt: {
    POST: async (request: gatedSchema.GatedEmailReceiptPOSTRequest) => {
      const apiMode = resolveApiMode()
      switch (apiMode) {
        case "gated": {
          const rsp = await post<rsp.GatedEmailReceiptPOSTResponse>({
            url: "/email-receipt",
            apiMode: "gated",
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.GatedEmailReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "park": {
          const rsp = await post<
            | parkSchema.ParkEmailPOSTSuccessResponse
            | parkSchema.ParkEmailPOSTErrorResponse
          >({
            url: "/email",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkEmailReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "reserve": {
          const rsp = await post<
            | reserveSchema.ReserveEmailPOSTSuccessResponse
            | reserveSchema.ReserveEmailPOSTErrorResponse
          >({
            url: "/email",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkEmailReceiptError(rsp.failReason)
          }
          return rsp
        }
        default: {
          console.error("Unhandled API Mode: ", resolveApiMode())
          throw new Error("Something went wrong, please try again later")
        }
      }
    },
  },
  receipt: {
    POST: async (request: gatedSchema.GatedReceiptPOSTRequest) => {
      const apiMode = resolveApiMode()
      switch (apiMode) {
        case "gated": {
          const rsp = await post<rsp.GatedReceiptPOSTResponse>({
            url: "/receipt",
            apiMode,
            data: request,
          })

          if ("failReason" in rsp) {
            throw new expectedErrors.GatedReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "park": {
          const rsp = await post<
            | parkSchema.ParkReceiptPOSTSuccessResponse
            | parkSchema.ParkReceiptPOSTErrorResponse
          >({
            url: "/receipt",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "reserve": {
          const rsp = await post<
            | parkSchema.ParkReceiptPOSTSuccessResponse
            | parkSchema.ParkReceiptPOSTErrorResponse
          >({
            url: "/receipt",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkReceiptError(rsp.failReason)
          }
          return rsp
        }
      }
    },
  },
  savedPaymentMethodActions: {
    POST: async (
      request: gatedSchema.GatedSavedPaymentMethodActionsPOSTRequest
    ) =>
      post<
        | gatedSchema.GatedSavedPaymentMethodActionsPOSTSuccessResponse
        | gatedSchema.GatedSavedPaymentMethodActionsPOSTErrorResponse
      >({
        url: "/saved-payment-method-actions",
        data: request,
      }),
  },
  savedPaymentMethods: {
    POST: async (request: gatedSchema.GatedSavedPaymentMethodsPOSTRequest) =>
      post<
        | gatedSchema.GatedSavedPaymentMethodsPOSTSuccessResponse
        | gatedSchema.GatedSavedPaymentMethodsPOSTErrorResponse
      >({
        url: "/saved-payment-methods",
        data: request,
      }),
  },
  setupIntentWallet: {
    POST: async (request: gatedSchema.GatedSetupIntentWalletPOSTRequest) =>
      post<
        | gatedSchema.GatedSetupIntentWalletPOSTSuccessResponse
        | gatedSchema.GatedSetupIntentWalletPOSTErrorResponse
      >({
        url: "/setup-intent-wallet",
        data: request,
      }),
  },
  textReceipt: {
    POST: async (request: gatedSchema.GatedTextReceiptPOSTRequest) => {
      const apiMode = resolveApiMode()
      switch (apiMode) {
        case "gated": {
          const rsp = await post<rsp.GatedTextReceiptPOSTResponse>({
            url: "/text-receipt",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.GatedTextReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "park": {
          const rsp = await post<
            | parkSchema.ParkTextPOSTSuccessResponse
            | parkSchema.ParkTextPOSTErrorResponse
          >({
            url: "/text",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkTextReceiptError(rsp.failReason)
          }
          return rsp
        }
        case "reserve": {
          const rsp = await post<
            | reserveSchema.ReserveTextPOSTSuccessResponse
            | reserveSchema.ReserveTextPOSTErrorResponse
          >({
            url: "/text",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkTextReceiptError(rsp.failReason)
          }
          return rsp
        }
      }
    },
  },
  memberships: {
    POST: async (request: reserveSchema.ReserveMembershipsPOSTRequest) =>
      post<
        | reserveSchema.ReserveMembershipsPOSTSuccessResponse
        | reserveSchema.ReserveMembershipsPOSTErrorResponse
      >({
        url: "/memberships",
        apiMode: "reserve",
        data: request,
      }),
  },
  theme: {
    POST: async (request: { qrLookupID: string }) =>
      post<
        | gatedSchema.GatedThemePOSTSuccessResponse
        | gatedSchema.GatedThemePOSTErrorResponse
      >({
        url: "/theme",
        data: request,
      }),
  },
  welcome: {
    POST: async (request: gatedSchema.GatedWelcomePOSTRequest) => {
      const apiMode = resolveApiMode()
      console.log("APIMODE: ", apiMode)
      switch (apiMode) {
        case "gated": {
          const rsp = await post<
            | gatedSchema.GatedWelcomePOSTSuccessResponse
            | gatedSchema.GatedWelcomePOSTErrorResponse
          >({
            url: "/welcome",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.GatedWelcomeError(rsp.failReason)
          }

          const epochTime = getCurrentEpochMillis()
          const newWelcomeToken: WelcomeToken = {
            qrLookupID: request.qr,
            token: rsp.token,
            epochTime,
          }
          console.log(`Storing GATED token with epoch: ${epochTime}`)
          welcomeTokenStorage.setSessionData(newWelcomeToken)
          break
        }
        case "park": {
          const rsp = await post<
            | parkSchema.ParkWelcomePOSTSuccessResponse
            | parkSchema.ParkWelcomePOSTErrorResponse
          >({
            url: "/welcome",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ParkWelcomeError(rsp.failReason)
          }
          const epochTime = getCurrentEpochMillis()
          const newWelcomeToken: WelcomeToken = {
            qrLookupID: request.qr,
            token: rsp.sessionToken,
            epochTime,
          }
          console.log(`Storing PARK token with epoch: ${epochTime}`)
          welcomeTokenStorage.setSessionData(newWelcomeToken)
          break
        }
        case "reserve": {
          const rsp = await post<
            | reserveSchema.ReserveWelcomePOSTSuccessResponse
            | reserveSchema.ReserveWelcomePOSTErrorResponse
          >({
            url: "/welcome",
            apiMode,
            data: request,
          })
          if ("failReason" in rsp) {
            throw new expectedErrors.ReserveWelcomeError(rsp.failReason)
          }
          const epochTime = getCurrentEpochMillis()
          const newWelcomeToken: WelcomeToken = {
            qrLookupID: request.qr,
            token: rsp.sessionToken,
            epochTime,
          }
          welcomeTokenStorage.setSessionData(newWelcomeToken)
          break
        }
      }
    },
  },
  reserve: {
    calendars: {
      POST: async (request: reserveSchema.ReserveCalendarsPOSTRequest) =>
        post<
          | reserveSchema.ReserveCalendarsPOSTSuccessResponse
          | reserveSchema.ReserveCalendarsPOSTErrorResponse
        >({
          url: "/calendars",
          apiMode: "reserve",
          data: request,
        }),
    },
    calendarAvailability: {
      POST: async (
        request: reserveSchema.ReserveCalendarAvailabilityPOSTRequest
      ) =>
        post<
          | reserveSchema.ReserveCalendarAvailabilityPOSTSuccessResponse
          | reserveSchema.ReserveCalendarAvailabilityPOSTErrorResponse
        >({
          url: "/calendar-availability",
          apiMode: "reserve",
          data: request,
        }),
    },
    calendarRates: {
      POST: async (request: reserveSchema.ReserveCalendarRatesPOSTRequest) =>
        post<
          | reserveSchema.ReserveCalendarRatesPOSTSuccessResponse
          | reserveSchema.ReserveCalendarRatesPOSTErrorResponse
        >({
          url: "/calendar-rates",
          apiMode: "reserve",
          data: request,
        }),
    },
    applyCode: {
      POST: async (request: reserveSchema.ReserveApplyCodePOSTRequest) =>
        post<
          | reserveSchema.ReserveApplyCodePOSTSuccessResponse
          | reserveSchema.ReserveApplyCodePOSTErrorResponse
        >({
          url: "/apply-code",
          apiMode: "reserve",
          data: request,
        }),
    },
    selectRate: {
      POST: async (request: reserveSchema.ReserveSelectRatePOSTRequest) =>
        post<
          | reserveSchema.ReserveSelectRatePOSTSuccessResponse
          | reserveSchema.ReserveSelectRatePOSTErrorResponse
        >({
          url: "/select-rate",
          apiMode: "reserve",
          data: request,
        }),
    },
    plate: {
      POST: async (request: reserveSchema.ReservePlatePOSTRequest) =>
        post<
          | reserveSchema.ReservePlatePOSTSuccessResponse
          | reserveSchema.ReservePlatePOSTErrorResponse
        >({
          url: "/plate",
          apiMode: "reserve",
          data: request,
        }),
    },
    checkout: {
      POST: async (request: reserveSchema.ReserveCheckoutPOSTRequest) =>
        post<
          | reserveSchema.ReserveCheckoutPOSTSuccessResponse
          | reserveSchema.ReserveCheckoutPOSTErrorResponse
        >({
          url: "/checkout",
          apiMode: "reserve",
          data: request,
        }),
    },
    promoPayment: {
      POST: async (request: reserveSchema.ReservePromoPaymentPOSTRequest) =>
        post<
          | reserveSchema.ReservePromoPaymentPOSTSuccessResponse
          | reserveSchema.ReservePromoPaymentPOSTErrorResponse
        >({
          url: "/promo-payment",
          apiMode: "reserve",
          data: request,
        }),
    },
    paymentIntent: {
      POST: async (request: reserveSchema.ReservePaymentIntentPOSTRequest) =>
        post<
          | reserveSchema.ReservePaymentIntentPOSTSuccessResponse
          | reserveSchema.ReservePaymentIntentPOSTErrorResponse
        >({
          url: "/payment-intent",
          apiMode: "reserve",
          data: request,
        }),
    },
    confirmPayment: {
      POST: async (request: reserveSchema.ReserveConfirmPaymentPOSTRequest) =>
        post<
          | reserveSchema.ReserveConfirmPaymentPOSTSuccessResponse
          | reserveSchema.ReserveConfirmPaymentPOSTErrorResponse
        >({
          url: "/confirm-payment",
          apiMode: "reserve",
          data: request,
        }),
    },
    setupIntent: {
      POST: async (request: reserveSchema.ReserveSetupIntentPOSTRequest) =>
        post<
          | reserveSchema.ReserveSetupIntentPOSTSuccessResponse
          | reserveSchema.ReserveSetupIntentPOSTErrorResponse
        >({
          url: "/setup-intent",
          apiMode: "reserve",
          data: request,
        }),
    },
    confirmSetupIntent: {
      POST: async (
        request: reserveSchema.ReserveConfirmSetupIntentPOSTRequest
      ) =>
        post<
          | reserveSchema.ReserveConfirmSetupIntentPOSTSuccessResponse
          | reserveSchema.ReserveConfirmSetupIntentPOSTErrorResponse
        >({
          url: "/confirm-setup-intent",
          apiMode: "reserve",
          data: request,
        }),
    },
    currentReservations: {
      POST: async (
        request: reserveSchema.ReserveCurrentReservationsPOSTRequest
      ) =>
        post<
          | reserveSchema.ReserveCurrentReservationsPOSTSuccessResponse
          | reserveSchema.ReserveCurrentReservationsPOSTErrorResponse
        >({
          url: "/current-reservations",
          apiMode: "reserve",
          data: request,
        }),
    },
    pastReservations: {
      POST: async (request: reserveSchema.ReservePastReservationsPOSTRequest) =>
        post<
          | reserveSchema.ReservePastReservationsPOSTSuccessResponse
          | reserveSchema.ReservePastReservationsPOSTErrorResponse
        >({
          url: "/past-reservations",
          apiMode: "reserve",
          data: request,
        }),
    },
    cancelReservation: {
      POST: async (
        request: reserveSchema.ReserveCancelReservationPOSTRequest
      ) =>
        post<
          | reserveSchema.ReserveCancelReservationPOSTSuccessResponse
          | reserveSchema.ReserveCancelReservationPOSTErrorResponse
        >({
          url: "/cancel-reservation",
          apiMode: "reserve",
          data: request,
        }),
    },
    plateUpdate: {
      POST: async (request: reserveSchema.ReservePlateUpdatePOSTRequest) =>
        post<
          | reserveSchema.ReservePlateUpdatePOSTSuccessResponse
          | reserveSchema.ReservePlateUpdatePOSTErrorResponse
        >({
          url: "/plate-update",
          apiMode: "reserve",
          data: request,
        }),
    },
    sessionStatus: {
      POST: async (request: reserveSchema.ReserveSessionStatusPOSTRequest) =>
        post<
          | reserveSchema.ReserveSessionStatusPOSTSuccessResponse
          | reserveSchema.ReserveSessionStatusPOSTErrorResponse
        >({
          url: "/session-status",
          apiMode: "reserve",
          data: request,
        }),
    },
  },
  park: {
    sessions: {
      POST: async (request: parkSchema.ParkSessionsPOSTRequest) =>
        post<
          | parkSchema.ParkSessionsPOSTSuccessResponse
          | parkSchema.ParkSessionsPOSTErrorResponse
        >({
          url: "/sessions",
          apiMode: "park",
          data: request,
        }),
    },
    validPasses: {
      POST: async (request: parkSchema.ParkValidPassesPOSTRequest) =>
        post<
          | parkSchema.ParkValidPassesPOSTSuccessResponse
          | parkSchema.ParkValidPassesPOSTErrorResponse
        >({
          url: "/valid-passes",
          apiMode: "park",
          data: request,
        }),
    },
    account: {
      POST: async (request: parkSchema.ParkAccountPOSTRequest) =>
        post<
          | parkSchema.ParkAccountPOSTSuccessResponse
          | parkSchema.ParkAccountPOSTErrorResponse
        >({
          url: "/account",
          apiMode: "park",
          data: request,
        }),
    },
    plate: {
      POST: async (request: parkSchema.ParkPlatePOSTRequest) =>
        post<
          | parkSchema.ParkPlatePOSTSuccessResponse
          | parkSchema.ParkPlatePOSTErrorResponse
        >({
          url: "/plate",
          apiMode: "park",
          data: request,
        }),
    },
    rates: {
      POST: async (request: parkSchema.ParkRatesPOSTRequest) =>
        post<
          | parkSchema.ParkRatesPOSTSuccessResponse
          | parkSchema.ParkRatesPOSTErrorResponse
        >({
          url: "/rates",
          apiMode: "park",
          data: request,
        }),
    },
    ratePassphrase: {
      POST: async (request: parkSchema.ParkRatePassphrasePOSTRequest) =>
        post<
          | parkSchema.ParkRatePassphrasePOSTSuccessResponse
          | parkSchema.ParkRatePassphrasePOSTErrorResponse
        >({
          url: "/rate-passphrase",
          apiMode: "park",
          data: request,
        }),
    },
    checkout: {
      POST: async (request: parkSchema.ParkCheckoutPOSTRequest) =>
        post<
          | parkSchema.ParkCheckoutPOSTSuccessResponse
          | parkSchema.ParkCheckoutPOSTErrorResponse
        >({
          url: "/checkout",
          apiMode: "park",
          data: request,
        }),
    },
    promo: {
      POST: async (request: parkSchema.ParkPromoPOSTRequest) =>
        post<
          | parkSchema.ParkPromoPOSTSuccessResponse
          | parkSchema.ParkPromoPOSTErrorResponse
        >({
          url: "/promo",
          apiMode: "park",
          data: request,
        }),
    },
    paymentIntent: {
      POST: async (request: parkSchema.ParkPaymentIntentPOSTRequest) =>
        post<
          | parkSchema.ParkPaymentIntentPOSTSuccessResponse
          | parkSchema.ParkPaymentIntentPOSTErrorResponse
        >({
          url: "/payment-intent",
          apiMode: "park",
          data: request,
        }),
    },
    paymentIntentElements: {
      POST: async (request: parkSchema.ParkPaymentIntentElementsPOSTRequest) =>
        post<
          | parkSchema.ParkPaymentIntentElementsPOSTSuccessResponse
          | parkSchema.ParkPaymentIntentElementsPOSTErrorResponse
        >({
          url: "/payment-intent-elements",
          apiMode: "park",
          data: request,
        }),
    },
    pay: {
      POST: async (request: parkSchema.ParkPayPOSTRequest) =>
        post<
          | parkSchema.ParkPayPOSTSuccessResponse
          | parkSchema.ParkPayPOSTErrorResponse
        >({
          url: "/pay",
          apiMode: "park",
          data: { ...request, isSamba: true },
        }),
    },
    passesForSale: {
      POST: async (request: parkSchema.ParkPassesForSalePOSTRequest) =>
        post<
          | parkSchema.ParkPassesForSalePOSTSuccessResponse
          | parkSchema.ParkPassesForSalePOSTErrorResponse
        >({
          url: "/passes-for-sale",
          apiMode: "park",
          data: request,
        }),
    },
    passes: {
      POST: async (request: parkSchema.ParkPassesPOSTRequest) =>
        post<
          | parkSchema.ParkPassesPOSTSuccessResponse
          | parkSchema.ParkPassesPOSTErrorResponse
        >({
          url: "/passes",
          apiMode: "park",
          data: request,
        }),
    },
    purchasePass: {
      POST: async (request: parkSchema.ParkPurchasePassPOSTRequest) =>
        post<
          | parkSchema.ParkPurchasePassPOSTSuccessResponse
          | parkSchema.ParkPurchasePassPOSTErrorResponse
        >({
          url: "/purchase-pass",
          apiMode: "park",
          data: { ...request, isSamba: true },
        }),
    },
    passRedeem: {
      POST: async (request: parkSchema.ParkPassRedeemPOSTRequest) =>
        post<
          | parkSchema.ParkPassRedeemPOSTSuccessResponse
          | parkSchema.ParkPassRedeemPOSTErrorResponse
        >({
          url: "/pass-redeem",
          apiMode: "park",
          data: request,
        }),
    },
    plateUpdate: {
      POST: async (request: parkSchema.ParkPlateUpdatePOSTRequest) =>
        post<
          | parkSchema.ParkPlateUpdatePOSTSuccessResponse
          | parkSchema.ParkPlateUpdatePOSTErrorResponse
        >({
          url: "/plate-update",
          apiMode: "park",
          data: request,
        }),
    },
    appUser: {
      POST: async (request: parkSchema.ParkAppUserPOSTRequest) =>
        post<
          | parkSchema.ParkAppUserPOSTSuccessResponse
          | parkSchema.ParkAppUserPOSTErrorResponse
        >({
          url: "/appuser",
          apiMode: "park",
          data: request,
        }),
      PUT: async (request: parkSchema.ParkAppUserPUTRequest) =>
        put<
          | parkSchema.ParkAppUserPUTSuccessResponse
          | parkSchema.ParkAppUserPUTErrorResponse
        >({
          url: "/appuser",
          apiMode: "park",
          data: request,
        }),
    },
    rateEndTime: {
      POST: async (request: parkSchema.ParkRateEndTimePOSTRequest) =>
        post<
          | parkSchema.ParkRateEndTimePOSTSuccessResponse
          | parkSchema.ParkRateEndTimePOSTErrorResponse
        >({
          url: "/rate-end-time",
          apiMode: "park",
          data: request,
        }),
    },
    calculateRate: {
      POST: async (request: parkSchema.ParkCalculateRatePOSTRequest) =>
        post<
          | parkSchema.ParkCalculateRatePOSTSuccessResponse
          | parkSchema.ParkCalculateRatePOSTErrorResponse
        >({
          url: "/calculate-rate",
          apiMode: "park",
          data: request,
        }),
    },
    sessionStatus: {
      POST: async (request: parkSchema.ParkSessionStatusPOSTRequest) => {
        const rsp = await post<
          | parkSchema.ParkSessionStatusPOSTSuccessResponse
          | parkSchema.ParkSessionStatusPOSTErrorResponse
        >({
          url: "/session-status",
          apiMode: "park",
          data: request,
        })

        if ("failReason" in rsp) {
          throw new expectedErrors.ParkSessionStatusError(rsp.failReason)
        }

        return rsp
      },
    },
  },
}

export default api
